import { inclusionFilterTypeMessageByValue } from 'constants/inclusions'
import { EmptyArray } from 'lib/array/arrayUtils'
import { buildHybridSearchFilterInclusions } from 'lib/offer/inclusionUtils'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'

interface SearchFilterCategoryInclusions {
  inclusions: Array<App.OfferInclusion>
  showMoreLabel: string | undefined
}

/**
 * Overflow copy must match the modal source list length, not mapped/deduped categories.
 */
export function getCompactInclusionsOverflowCount(
  originalListCount: number,
  visibleCategoryCount: number,
): number | undefined {
  if (originalListCount <= visibleCategoryCount) return undefined
  return originalListCount
}

/**
 * When mapping is on, use the hybrid mapped/original rows. Otherwise keep the source list.
 */
export function resolveMappedSearchFilterInclusions<T>(
  shouldMap: boolean,
  mappedInclusions: Array<T>,
  originalInclusions: Array<T>,
): Array<T> {
  if (!shouldMap) return originalInclusions
  return mappedInclusions
}

export function shouldShowCompactMappedInclusionsOverflow(
  shouldMap: boolean,
  originalCount: number,
  visibleHybridCount: number,
): boolean {
  return shouldMap && originalCount > visibleHybridCount
}

export function useSearchFilterCategoryInclusions(
  inclusions: Array<App.OfferInclusion> | undefined,
  maxCategories: number,
): SearchFilterCategoryInclusions {
  const intl = useIntl()

  return useMemo(() => {
    if (!inclusions?.length) {
      return {
        inclusions: EmptyArray,
        showMoreLabel: undefined,
      }
    }

    const hybridInclusions = buildHybridSearchFilterInclusions(
      inclusions,
      maxCategories,
      (filterType) => intl.formatMessage(inclusionFilterTypeMessageByValue[filterType]),
    )
    const overflowCount = getCompactInclusionsOverflowCount(inclusions.length, hybridInclusions.length)
    const showMoreLabel = overflowCount === undefined ?
      undefined :
        intl.formatMessage({
          id: '32bp-KWU9dPrrdireNEl',
          defaultMessage: 'View all {count} inclusions',
          description: 'Link on compact search tiles to open the full original inclusions list shown in the inclusions modal',
        }, { count: overflowCount })

    return {
      inclusions: hybridInclusions,
      showMoreLabel,
    }
  }, [inclusions, intl, maxCategories])
}
